import { useEffect, useRef, useCallback } from 'react';
import { checkTokenAuth } from 'shared/lib/authUtils';
import { logoutSession } from 'shared/lib/logoutSession';

const useActivityTracker = (timeout = 30 * 60 * 60 * 1000) => {
  const timeoutRef = useRef(null);
  const isActiveRef = useRef(true);
  const lastActivityRef = useRef(Date.now());
  const isTrackingRef = useRef(false);
  const isPausedRef = useRef(false);
  const eventsRef = useRef([
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click',
  ]);

  const isAuthenticated = useCallback(() => {
    return !!checkTokenAuth();
  }, []);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    isActiveRef.current = true;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (isPausedRef.current || !isTrackingRef.current) {
      console.log('Timer not set - tracking paused or stopped');
      return;
    }

    timeoutRef.current = setTimeout(() => {
      if (!isAuthenticated()) {
        console.log('User not authenticated, skipping auto logout');
        return;
      }

      isActiveRef.current = false;
      console.log('User inactive timeout reached');
    }, timeout);
  }, [timeout]);

  const handleActivity = useCallback(() => {
    if (!isTrackingRef.current || isPausedRef.current || !isAuthenticated()) {
      return;
    }

    resetTimer();
  }, [resetTimer]);

  const isUserActive = useCallback(() => {
    return isActiveRef.current && isAuthenticated();
  }, []);

  const getLastActivity = useCallback(() => {
    return lastActivityRef.current;
  }, []);

  const forceLogout = useCallback(() => {
    if (!isAuthenticated()) {
      console.log('User already logged out, skipping force logout');
      return;
    }

    console.log('Force logout initiated');
    logoutSession();
  }, []);

  const getRemainingTime = useCallback(() => {
    if (!isAuthenticated()) return 0;

    const timeSinceLastActivity = Date.now() - lastActivityRef.current;
    const remaining = timeout - timeSinceLastActivity;
    return Math.max(0, remaining);
  }, [timeout]);

  const startTracking = useCallback(() => {
    if (isTrackingRef.current) {
      console.warn('Activity tracking already active');
      return;
    }

    eventsRef.current.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    isTrackingRef.current = true;
    isPausedRef.current = false;
    resetTimer();
    console.warn('Activity tracking started');
  }, [handleActivity, resetTimer]);

  const stopTracking = useCallback(() => {
    if (!isTrackingRef.current) {
      console.warn('Activity tracking already stopped');
      return;
    }

    eventsRef.current.forEach((event) => {
      document.removeEventListener(event, handleActivity, true);
    });

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    isTrackingRef.current = false;
    isPausedRef.current = false;
    console.warn('Activity tracking stopped');
  }, [handleActivity]);

  const pauseTracking = useCallback(() => {
    isPausedRef.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    console.warn('Activity tracking paused');
  }, []);

  const resumeTracking = useCallback(() => {
    isPausedRef.current = false;
    if (isTrackingRef.current) {
      resetTimer();
      console.warn('Activity tracking resumed');
    }
  }, [resetTimer]);

  useEffect(() => {
    return () => {
      stopTracking();
    };
  }, [stopTracking]);

  return {
    isUserActive,
    getLastActivity,
    forceLogout,
    resetTimer,
    getRemainingTime,
    startTracking,
    stopTracking,
    pauseTracking,
    resumeTracking,
    isTracking: () => isTrackingRef.current,
    isPaused: () => isPausedRef.current,
  };
};

export default useActivityTracker;
